import {
	GeneralGlobalCreationItemKind,
	GlobalCreationItem,
	GlobalCreationItemKind,
	HubCreationItemKind,
	HubCreationItem,
	list
} from "client/Api/GlobalCreation/globalCreation";
import { useEditContactDialog } from "client/Features/AddressBook/Edit/EditDialog";
import { useNewDocumentDialog } from "client/Features/Documents/Dialogs/NewDocumentDialog";
import { isNotNil } from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo
} from "react";
import create, { UseStore } from "zustand";
import createVanilla, { StoreApi } from "zustand/vanilla";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { useAnalyticsStore } from "../Analytics/analyticsStore";
import { AppType } from "client/Api/Apps/common";
import { useCreateHubItemDialog } from "../Hub/CreateHubItemDialog/CreateHubItemDialog";
import { useComposeAppointmentDialog } from "../Calendar/ComposeAppointmentDialog/ComposeAppointmentDialog";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { useStartCreateSpace } from "../Workspace/useStartCreateSpace";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { SaveCommunityResult, useEditCommunityDialog } from "../Hub/Communities/EditCommunityDialog/EditCommunityDialog";
import { useCreateCommunityPostDialog } from "../Hub/Communities/Composer/CreateCommunityPostDialog";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { getHubUrlForCommunity } from "../Hub/Communities/CommunitiesPage";
import { HubDataSource } from "client/Api/Hub/hub";
import { CommunityPostDetailsViewModel } from "client/Api/Communities/communityPostViewer";

type GlobalCreationStoreData = {
	items: readonly GlobalCreationItem[] | undefined;
	wasInitialized: boolean;
	ensureInitialized(): void;
};

const store: StoreApi<GlobalCreationStoreData> = createVanilla(
	(set, get): GlobalCreationStoreData => {
		return {
			items: undefined,
			wasInitialized: false,
			ensureInitialized: () => {
				if (get().wasInitialized) {
					return;
				}

				set({
					wasInitialized: true
				});

				list()
					.then(
						(items) => set({
							items
						}),
						() => set({
							items: []
						})
					);
			}
		};
	}
);

const useStore: UseStore<GlobalCreationStoreData> = create(store);

export function useGlobalCreationItems():
	readonly GlobalCreationItem[] | undefined;
export function useGlobalCreationItems<TResult>(
	transformer: (item: GlobalCreationItem) => TResult
):
	readonly TResult[] | undefined;
export function useGlobalCreationItems<TResult = GlobalCreationItem>(
	transformer?: (item: GlobalCreationItem) => TResult
):
	readonly TResult[] | undefined
{
	const { items, ensureInitialized } = useStore(
		(x) => ({
			items: x.items,
			ensureInitialized: x.ensureInitialized
		}),
		shallowComparison
	);

	useEffect(
		(): void => {
			ensureInitialized();
		},
		[ensureInitialized]
	);

	const transformedItems = useMemo(
		(): readonly TResult[] | undefined => {
			if (!items || !transformer) {
				return items as TResult[];
			}

			return items.map(transformer);
		},
		[items, transformer]
	);

	return transformedItems;
}

export function useCreationHandler(): [
	element: ReactElement | null,
	open: (
		item: GlobalCreationItem,
		onClose?: () => void
	) => Promise<void>
] {
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);

	const emailClient = useMemo(
		(): EmailClient => getEffectiveEmailClient(),
		[]
	);

	const [newDocumentDialog, openNewDocumentDialog] = useNewDocumentDialog();
	const [editContactDialog, openEditContactDialog] = useEditContactDialog();
	const [composeAppointmentDialog, openComposeAppointmentDialog] = useComposeAppointmentDialog();

	const userTimezone: TimeZone = useUserTimezone();
	const [startCreateSpaceElement, startCreateSpace] = useStartCreateSpace();

	const {
		element: hubCreationElement,
		openItem: openHubCreationItemHandler
	} = useOpenHubCreationItem();

	const openHandler = useCallback(
		async (
			item: GlobalCreationItem,
			onClose?: () => void
		): Promise<void> => {
			const appType: AppType | null = getAppType(item);

			if (isNotNil(appType)) {
				addAnalyticsAppEvent({ appType });
			}

			switch (item.kind) {
				case GlobalCreationItemKind.General:
					switch (item.itemKind) {
						case GeneralGlobalCreationItemKind.Email:
							if (emailClient.supportsEmailCompose) {
								emailClient.composeEmail();
							} else {
								console.error("The backend and frontend don't agree on the availability of the email option");
							}
							break;
						case GeneralGlobalCreationItemKind.Appointment:
							await openComposeAppointmentDialog({
								timezone: userTimezone
							});
							break;
						case GeneralGlobalCreationItemKind.PersonalContact:
							await openEditContactDialog({});
							break;
						case GeneralGlobalCreationItemKind.SharedSpace:
							await startCreateSpace();
							break;
						default:
							never(item);
					}
					onClose?.();
					break;
				case GlobalCreationItemKind.Hub:
					await openHubCreationItemHandler(
						item,
						onClose
					);
					break;
				case GlobalCreationItemKind.File:
					await openNewDocumentDialog({
						newDocumentType: item.itemKind
					});
					onClose?.();
					break;
				default:
					never(item);
			}
		},
		[
			emailClient,
			userTimezone,
			openEditContactDialog,
			openNewDocumentDialog,
			addAnalyticsAppEvent,
			openComposeAppointmentDialog,
			startCreateSpace,
			openHubCreationItemHandler
		]
	);

	const element = useMemo(
		(): ReactElement | null => {
			return [
				composeAppointmentDialog,
				newDocumentDialog,
				editContactDialog,
				startCreateSpaceElement,
				hubCreationElement
			]
				.find(isNotNil) ?? null;
		},
		[
			editContactDialog,
			newDocumentDialog,
			composeAppointmentDialog,
			startCreateSpaceElement,
			hubCreationElement
		]
	);

	return [
		element,
		openHandler
	];
}

type OpenHubCreationItemResult = {
	element: ReactElement | null;
	openItem: (
		item: HubCreationItem,
		onClose?: () => void
	) => Promise<void>;
};

function useOpenHubCreationItem(): OpenHubCreationItemResult {
	const [createHubItemDialog, openCreateHubItemDialog] = useCreateHubItemDialog();
	const [editCommunityDialog, openEditCommunityDialog] = useEditCommunityDialog();
	const [createCommunityPostDialog, openCreateCommunityPostDialog] = useCreateCommunityPostDialog();

	const openItem = useCallback(
		async (
			item: HubCreationItem,
			onClose?: () => void
		): Promise<void> => {
			switch (item.itemKind) {
				case HubCreationItemKind.Announcement:
				case HubCreationItemKind.Event:
				case HubCreationItemKind.KnowledgeItem:
					if (onClose) {
						await openCreateHubItemDialog({
							kind: hubCreationKindToHubItemKindMap[item.itemKind],
							closeDialogOn: "back",
							onClose
						});
					} else {
						await openCreateHubItemDialog({
							kind: hubCreationKindToHubItemKindMap[item.itemKind]
						});
					}
					break;
				case HubCreationItemKind.Community:
					const editCommunityResult: TryResult<SaveCommunityResult> =
						await openEditCommunityDialog({});

					onClose?.();

					if (!editCommunityResult.wasSuccessful) {
						return;
					}

					navigateTo(
						getHubUrlForCommunity({
							source: HubDataSource.Community,
							communityId: editCommunityResult
								.result
								.id
						})
					);
					break;
				case HubCreationItemKind.CommunityPost:
					const createCommunityPostResult: TryResult<CommunityPostDetailsViewModel> =
						await openCreateCommunityPostDialog({});

					onClose?.();

					if (!createCommunityPostResult.wasSuccessful) {
						return;
					}

					navigateTo(
						getHubUrlForCommunity({
							source: HubDataSource.Community,
							communityId: createCommunityPostResult
								.result
								.community
								.id
						})
					);
					break;
				default:
					never(item.itemKind);
			}
		},
		[
			openCreateCommunityPostDialog,
			openCreateHubItemDialog,
			openEditCommunityDialog
		]
	);

	const element = useMemo(
		(): ReactElement | null => {
			return [
				createHubItemDialog,
				editCommunityDialog,
				createCommunityPostDialog
			]
				.find(isNotNil) ?? null;
		},
		[
			createHubItemDialog,
			editCommunityDialog,
			createCommunityPostDialog
		]
	);

	return {
		element,
		openItem
	};
}

function getAppType(
	item: GlobalCreationItem
): AppType | null {
	switch(item.kind) {
		case GlobalCreationItemKind.File:
			return AppType.Documents;
		case GlobalCreationItemKind.Hub:
			return AppType.Hub;
		case GlobalCreationItemKind.General:
			switch (item.itemKind) {
				case GeneralGlobalCreationItemKind.Appointment:
					return AppType.Office365Calendar;
				case GeneralGlobalCreationItemKind.Email:
					return AppType.Email;
				case GeneralGlobalCreationItemKind.PersonalContact:
					return AppType.AddressBook;
				case GeneralGlobalCreationItemKind.SharedSpace:
					return null;
				default:
					return never(item);
			}
		default:
			never(item);
	}
}

type HubCreationItemKindWithoutCommunity = Exclude<
	HubCreationItemKind,
	| HubCreationItemKind.Community
	| HubCreationItemKind.CommunityPost
>;

const hubCreationKindToHubItemKindMap: Record<HubCreationItemKindWithoutCommunity, HubItemKind> = {
	[HubCreationItemKind.Announcement]: HubItemKind.Announcement,
	[HubCreationItemKind.Event]: HubItemKind.Event,
	[HubCreationItemKind.KnowledgeItem]: HubItemKind.KnowledgeItem,
};